import type {
	InteractionManagerProject,
	InteractionManagerProjectFull,
	V2ListProjectsRequest,
} from 'InteractionManager-sdk';
import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';
import { InteractionManager } from '@/SDK/InteractionManager';

interface ProjectContextType {
	projects: any[];
	selectedProject: InteractionManagerProjectFull | null;
	refreshProjects: () => Promise<void>;
	setSelectedProject: (project: any | null) => void;
	selectProjectById: (projectId: string) => void;
	ulds: any[];
	setSelectedULDType: (uld: any | null) => void;
	selectedULDType: any | null;
	boxes: any;
	setSelectedBoxIds: (boxes: any | null) => void;
	selectedBoxIds: any;
	selectedBinIds: string[];
	setSelectedBinIds: (binIds: string[]) => void;
	viewMode: 'normal' | '3d';
	setViewMode: (mode: 'normal' | '3d') => void;
	selected3DULD: any | null;
	setSelected3DULD: (uld: any) => void;
	totalProjects: number;
	page: number;
	pageSize: number;
	setPage: (page: number) => void;
	allProjects: InteractionManagerProject[];
}

const ProjectContext = createContext<ProjectContextType | null>(null);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [selectedProject, setSelectedProject] = useState<any | null>(null);
	const [ulds, setULDs] = useState<any | null>(null);
	const [selectedULDType, setSelectedULDType] = useState<any | null>([]);
	const [selectedBoxIds, setSelectedBoxIds] = useState<string[]>([]);
	const [boxes, setBoxes] = useState<string[]>([]);
	const [selectedBinIds, setSelectedBinIds] = useState<string[]>([]);
	const [viewMode, setViewMode] = useState<'normal' | '3d'>('normal');
	const [selected3DULD, setSelected3DULD] = useState<any | null>(null);
	const [page, setPage] = useState(1);
	const [allProjects, setAllProjects] = useState<any[]>([]);
	const pageSize = 15;

	const im: InteractionManager = InteractionManager.getInstance();

	// Derive paginated projects from allProjects
	const projects = useMemo(() => {
		const skip = (page - 1) * pageSize;
		return allProjects.slice(skip, skip + pageSize);
	}, [allProjects, page]);

	const totalProjects = allProjects.length;

	const refreshProjects = useCallback(async () => {
		try {
			const listRes = await im.API.listProjects({
				Skip: 0,
				Limit: 10000,
			} as V2ListProjectsRequest);

			if (!listRes.Error) {
				setAllProjects(listRes.Payload || []);
			}
		} catch (e) {
			console.log('refreshProjects error →', e);
		}
	}, []);

	const selectProjectById = useCallback(
		(projectId: string) => {
			const project = allProjects.find((p) => String(p._id) === projectId);
			setSelectedProject(project || null);
		},
		[allProjects],
	);

	const contextValue = useMemo(
		() => ({
			projects,
			selectedProject,
			refreshProjects,
			setSelectedProject,
			selectProjectById,
			totalProjects,
			page,
			pageSize,
			setPage,
			ulds,
			setSelectedULDType,
			selectedULDType,
			selectedBoxIds,
			setSelectedBoxIds,
			selectedBinIds,
			setSelectedBinIds,
			viewMode,
			setViewMode,
			selected3DULD,
			setSelected3DULD,
			allProjects,
		}),
		[
			projects,
			selectedProject,
			refreshProjects,
			selectProjectById,
			totalProjects,
			page,
			ulds,
			selectedULDType,
			selectedBoxIds,
			selectedBinIds,
			viewMode,
			selected3DULD,
			allProjects,
		],
	);

	return (
		<ProjectContext.Provider value={contextValue}>
			{children}
		</ProjectContext.Provider>
	);
};

export const useProjects = () => {
	const ctx = useContext(ProjectContext);
	if (!ctx) throw new Error('useProjects must be used inside ProjectProvider');
	return ctx;
};
